/**
 * Short-lived world effects: the dust a touchdown or a take-off kicks up, and
 * the tyre marks a landing leaves behind.
 *
 * Both come from fixed pools: a landing every second or two must not leave the
 * scene growing meshes, and a pooled burst also means one material per puff
 * instead of one per particle.
 */

import { Group, Mesh, MeshBasicMaterial, PlaneGeometry } from "three";
import type { Vec2 } from "../airport/AirportDefinition";
import { FEEDBACK } from "../config/gameplay";
import { LAYER, PALETTE } from "../config/palette";

/** Bursts alive at once; the oldest is recycled when they are all busy. */
const POOL_SIZE = 4;
/** Skid marks outlive their dust, so they need a deeper pool. */
const SKID_POOL_SIZE = 6;
const TAU = Math.PI * 2;
/** Just over the runway paint, under the drawn routes. */
const DUST_Z = LAYER.runway + 0.12;
/** Under the dust: the rubber is on the tarmac, the dust is above it. */
const SKID_Z = LAYER.runway + 0.05;

interface Particle {
  readonly mesh: Mesh;
  dirX: number;
  dirY: number;
  reach: number;
}

interface Burst {
  readonly group: Group;
  readonly material: MeshBasicMaterial;
  readonly particles: Particle[];
  startedAt: number;
  live: boolean;
}

interface Skid {
  readonly group: Group;
  readonly material: MeshBasicMaterial;
  readonly tracks: Mesh[];
  startedAt: number;
  live: boolean;
}

/** Stable spread per burst, so two puffs in the same spot do not match. */
function jitter(position: Vec2): number {
  const value =
    Math.sin(position.x * 12.9898 + position.y * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

export class EffectsView {
  readonly group = new Group();

  private readonly geometry = new PlaneGeometry(1, 1);
  private readonly bursts: Burst[] = [];
  private readonly skids: Skid[] = [];
  private clock = 0;
  private lastTime: number | null = null;

  constructor() {
    this.group.name = "effects";
  }

  /**
   * Advance the effect clock.
   *
   * Driven off the same wall clock the aircraft animate on, but paused with
   * the game — otherwise a puff started just before a pause is over by the
   * time the player comes back.
   */
  update(time: number, paused = false): void {
    const delta =
      this.lastTime === null
        ? 0
        : Math.max(0, Math.min(0.25, time - this.lastTime));
    this.lastTime = time;
    if (!paused) {
      this.clock += delta;
    }

    for (const burst of this.bursts) {
      if (!burst.live) {
        continue;
      }
      const progress = (this.clock - burst.startedAt) / FEEDBACK.dustSeconds;
      if (progress >= 1) {
        burst.live = false;
        burst.group.visible = false;
        continue;
      }
      this.drawBurst(burst, Math.max(0, progress));
    }

    for (const skid of this.skids) {
      if (!skid.live) {
        continue;
      }
      const progress = (this.clock - skid.startedAt) / FEEDBACK.skidSeconds;
      if (progress >= 1) {
        skid.live = false;
        skid.group.visible = false;
        continue;
      }
      drawSkid(skid, Math.max(0, progress));
    }
  }

  /**
   * Kick up a puff of dust at a world position.
   *
   * With no heading the particles go out in a ring, which is what a touchdown
   * looks like. Given one, they are thrown into a cone *behind* that heading —
   * the take-off case, where the dust has a direction.
   */
  burst(position: Vec2, heading: number | null = null): void {
    const burst = this.take();
    burst.group.position.set(position.x, position.y, DUST_Z);
    burst.group.visible = true;
    burst.startedAt = this.clock;
    burst.live = true;

    const spin = jitter(position);
    const count = burst.particles.length;
    for (let index = 0; index < count; index++) {
      const particle = burst.particles[index]!;
      const spread = count > 1 ? index / (count - 1) : 0.5;
      const angle =
        heading === null
          ? spin * TAU + (index / count) * TAU
          : heading +
            Math.PI +
            (spread - 0.5) * 2 * FEEDBACK.dustConeRadians +
            (spin - 0.5) * 0.2;
      particle.dirX = Math.cos(angle);
      particle.dirY = Math.sin(angle);
      // Alternating reach keeps the spray from reading as a solid arc.
      particle.reach = FEEDBACK.dustSpread * (index % 2 === 0 ? 1 : 0.62);
    }
    this.drawBurst(burst, 0);
  }

  /** Lay two tyre marks down the roll direction, which then fade. */
  skid(position: Vec2, heading: number): void {
    const skid = this.takeSkid();
    skid.group.position.set(position.x, position.y, SKID_Z);
    skid.group.rotation.z = heading;
    skid.group.visible = true;
    skid.startedAt = this.clock;
    skid.live = true;
    drawSkid(skid, 0);
  }

  private drawBurst(burst: Burst, progress: number): void {
    // Fast out, slow to a stop — dust is thrown, not blown.
    const eased = 1 - (1 - progress) * (1 - progress);
    const fade = 1 - progress;
    burst.material.opacity = 0.62 * fade * fade;

    for (const particle of burst.particles) {
      const distance = particle.reach * eased;
      particle.mesh.position.set(
        particle.dirX * distance,
        particle.dirY * distance,
        0,
      );
      particle.mesh.scale.setScalar(FEEDBACK.dustSize * (0.55 + eased * 0.85));
    }
  }

  private take(): Burst {
    const free = this.bursts.find((burst) => !burst.live);
    if (free) {
      return free;
    }
    if (this.bursts.length < POOL_SIZE) {
      const created = this.create();
      this.bursts.push(created);
      this.group.add(created.group);
      return created;
    }
    // All busy: the one that started earliest has the least left to show.
    return this.bursts.reduce((oldest, burst) =>
      burst.startedAt < oldest.startedAt ? burst : oldest,
    );
  }

  private takeSkid(): Skid {
    const free = this.skids.find((skid) => !skid.live);
    if (free) {
      return free;
    }
    if (this.skids.length < SKID_POOL_SIZE) {
      const created = this.createSkid();
      this.skids.push(created);
      this.group.add(created.group);
      return created;
    }
    return this.skids.reduce((oldest, skid) =>
      skid.startedAt < oldest.startedAt ? skid : oldest,
    );
  }

  private create(): Burst {
    const group = new Group();
    group.visible = false;
    const material = new MeshBasicMaterial({
      color: PALETTE.scenery.dirt,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });

    const particles: Particle[] = [];
    for (let index = 0; index < FEEDBACK.dustParticles; index++) {
      const mesh = new Mesh(this.geometry, material);
      mesh.renderOrder = 5;
      group.add(mesh);
      particles.push({ mesh, dirX: 1, dirY: 0, reach: 0 });
    }

    return { group, material, particles, startedAt: 0, live: false };
  }

  private createSkid(): Skid {
    const group = new Group();
    group.name = "skid";
    group.visible = false;
    const material = new MeshBasicMaterial({
      color: PALETTE.aircraftDark,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });

    const tracks: Mesh[] = [];
    for (const side of [-1, 1]) {
      const mesh = new Mesh(this.geometry, material);
      mesh.renderOrder = 4;
      mesh.position.y = (side * FEEDBACK.skidGauge) / 2;
      group.add(mesh);
      tracks.push(mesh);
    }

    return { group, material, tracks, startedAt: 0, live: false };
  }

  dispose(): void {
    for (const burst of this.bursts) {
      burst.material.dispose();
      this.group.remove(burst.group);
    }
    for (const skid of this.skids) {
      skid.material.dispose();
      this.group.remove(skid.group);
    }
    this.bursts.length = 0;
    this.skids.length = 0;
    this.geometry.dispose();
  }
}

/**
 * Marks are laid down as the wheels roll, so they grow before they fade: a
 * mark at full length on the first frame reads as a decal, not as rubber.
 */
function drawSkid(skid: Skid, progress: number): void {
  const laid = Math.min(1, progress / 0.22);
  const length = FEEDBACK.skidLength * (0.3 + laid * 0.7);
  const fade = 1 - progress;
  skid.material.opacity = 0.3 * fade;

  for (const track of skid.tracks) {
    track.scale.set(length, FEEDBACK.skidWidth, 1);
    // The quad grows forward from the touchdown point, not around it.
    track.position.x = length / 2;
  }
}
